import React, { useEffect } from 'react';
import './WhitePaper.css';
import PixelIcon from './PixelIcon';

function WhitePaper({ onClose, section }) {
  useEffect(() => {
    if (!section) return;
    const el = document.getElementById(`wp-${section}`);
    if (el) el.scrollIntoView({ block: 'start' });
  }, [section]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Prevent click propagation from modal to overlay
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="whitepaper-overlay" onClick={onClose}>
      <div className="whitepaper-modal" onClick={handleModalClick} role="dialog" aria-modal="true" aria-label="Methodology">
        <div className="whitepaper-header">
          <div className="whitepaper-title">
            <span className="title-icon"><PixelIcon name="doc" size={18} /></span>
            SYSTEM ARCHITECTURE & METHODOLOGY
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close"><PixelIcon name="close" size={14} /></button>
        </div>
        
        <div className="whitepaper-content">
          <div className="whitepaper-section">
            <h2>THEORETICAL FRAMEWORK</h2>
            <p>
              AI-MAGEDDON monitors the global AI landscape against the two trajectories described in the "AI 2027" scenario: a "Race" (uncontrolled acceleration, with safety losing to competition) and a "Slowdown" (coordination, oversight and safety holding the pace back).
            </p>
            <p>
              It reads the world through news coverage: every hour it collects AI-related articles, scores how each one moves the world toward one trajectory or the other, and aggregates those signals into seven dimensions and two indices.
            </p>
          </div>

          <div className="whitepaper-section">
            <h2>SYSTEM ARCHITECTURE</h2>
            <ol className="pipeline">
              <li><strong>Collect:</strong> every hour, 14 RSS feeds (TechCrunch, Reuters, Defense News, MIT Technology Review, BBC, South China Morning Post and others). Duplicates are dropped.</li>
              <li><strong>Filter:</strong> a small, fast model discards articles with no real AI relevance.</li>
              <li><strong>Classify:</strong> a Large Language Model assigns each remaining article to one dimension, with an impact from -1.0 (slowdown signal) to +1.0 (race signal) and a short rationale (shown in each event card).</li>
              <li><strong>Aggregate:</strong> the index engine recomputes the seven dimensions and the two indices from the last 90 days of events.</li>
              <li><strong>Record:</strong> a snapshot is stored every day at midnight UTC; that history is the line in the Temporal Evolution chart.</li>
              <li><strong>Watch:</strong> a watchdog raises alerts when an index or a dimension crosses its threshold.</li>
            </ol>
          </div>

          <div className="whitepaper-section">
            <h2>DIMENSIONAL ANALYSIS</h2>
            <p>Seven dimensions, each on a 0-3 scale. They come in two kinds, which matters for how to read them.</p>

            <h3 className="dimension-group-title">Accelerators: a high value pushes toward the race</h3>
            <div className="dimension-grid">
              <div className="dimension-card">
                <h3>Military Integration</h3>
                <p>AI adoption in defense and intelligence: contracts, autonomous weapons, military AI programs.</p>
              </div>
              <div className="dimension-card">
                <h3>Centralization</h3>
                <p>Concentration of AI capabilities in a few companies or states: acquisitions, market share, compute ownership.</p>
              </div>
              <div className="dimension-card">
                <h3>Economic Dependency</h3>
                <p>How structurally the economy relies on AI: job displacement, productivity, AI valuations.</p>
              </div>
            </div>

            <h3 className="dimension-group-title">Brakes: a high value slows the race down, a low value is the warning sign</h3>
            <div className="dimension-grid">
              <div className="dimension-card">
                <h3>Model Governance</h3>
                <p>Oversight of model development: AI Act implementation, audits, safety committees, liability rules.</p>
              </div>
              <div className="dimension-card">
                <h3>Compute Control</h3>
                <p>Control over AI hardware: export restrictions, data center rules. Smuggling and loopholes lower it.</p>
              </div>
              <div className="dimension-card">
                <h3>International Cooperation</h3>
                <p>Global coordination vs. tension: treaties and summits raise it, sanctions and arms-race rhetoric lower it.</p>
              </div>
              <div className="dimension-card">
                <h3>Public Narrative</h3>
                <p>Public sentiment: critical discourse and demands for safety raise it, uncritical hype lowers it.</p>
              </div>
            </div>
          </div>

          <div className="whitepaper-section">
            <h2>METHODOLOGY <span className="version-tag">INDEX ALGORITHM v2.0</span></h2>
            <ul>
              <li>
                <strong>Dimension Values (0-3):</strong> Each dimension is recomputed from the last 90 days of events. Recent news weighs more (an event counts half after 21 days), and a dimension with little evidence stays close to the neutral 1.5. Dimensions are either <em>accelerators</em> (Military Integration, Centralization, Economic Dependency: race news raises them) or <em>brakes</em> (Model Governance, Compute Control, International Cooperation, Critical Public Narrative: race news lowers them).
              </li>
              <li>
                <strong>Index Calculation (0-3):</strong>
                <br/>
                <em>Race Index</em> = strength of the accelerators: 40% Military Integration + 30% Centralization + 30% Economic Dependency.
                <br/>
                <em>Slowdown Index</em> = strength of the brakes: 35% Model Governance + 25% Compute Control + 25% International Cooperation + 15% Public Narrative.
                <br/>
                The two indices use separate dimensions, so they can rise or fall together.
              </li>
            </ul>
          </div>

          <div className="whitepaper-section">
            <h2>AI 2027 MILESTONES</h2>
            <p>
              The Temporal Evolution chart marks the 15 dated milestones of the AI 2027 scenario, from "Mid 2025: Stumbling Agents" to "October 2027: Government Oversight" (E15). E15 is the branch point where the scenario splits into a race ending and a slowdown ending.
            </p>
            <p>
              Solid markers are milestones already passed, dashed ones are still ahead. They are a narrative reference to compare with the measured indices, not events detected by the system. The chart shows no projections: only measured values.
            </p>
          </div>

          <div className="whitepaper-section">
            <h2>SCENARIO ALERTING</h2>
            <p>
              The status compares the two indices: <strong>"Race Dominant"</strong> when the Race Index exceeds the Slowdown Index by more than 0.25, <strong>"Slowdown Dominant"</strong> in the opposite case, <strong>"Balanced"</strong> in between. Confidence expresses the gap as a share of its maximum (1.5 points).
            </p>
            <p>
              The danger level follows the Race Index alone: <strong>CAUTION</strong> from 1.5, <strong>DANGER</strong> from 2.4. The watchdog opens an alert when an index passes 2.5, an accelerator reaches 2.8 or a brake falls to 0.2, and closes it only once the value has moved back by a margin (hysteresis), so a value hovering on a threshold does not trigger repeated alerts.
            </p>
          </div>

          <div className="whitepaper-section" id="wp-api">
            <h2>PUBLIC API</h2>
            <p>
              The indices are available as open data through a read-only JSON API. No key is needed; responses are cached for 5 minutes. Data is released under <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC BY 4.0</a>: free to use, including commercially, with credit to <strong>AI-mageddon (ai27.org)</strong>.
            </p>
            <ul className="api-list">
              <li><code>GET /api/v1/index</code> current Race and Slowdown indices, status, level and the seven dimensions</li>
              <li><code>GET /api/v1/history?from=2026-01-01&amp;to=2026-10-05</code> daily snapshots (default: last 365 days)</li>
              <li><code>GET /api/v1/dimensions</code> definitions, kind (accelerator or brake) and current values</li>
              <li><code>GET /api/v1/events?limit=20&amp;dimension=model_governance</code> latest analyzed news (max 100)</li>
              <li><code>GET /api/v1/methodology</code> weights and parameters of the algorithm</li>
            </ul>
            <pre className="api-example">{`curl https://ai27.org/api/v1/index

{ "data": { "raceIndex": 2.52, "slowdownIndex": 1.66,
            "status": "RACE_DOMINANT", "level": "DANGER", ... },
  "meta": { "apiVersion": "1.0", "algorithmVersion": "2.0",
            "license": { "name": "CC BY 4.0", ... } } }`}</pre>
            <p>Every response carries <code>meta.algorithmVersion</code>: when the algorithm changes, history is recalculated and the changelog below says why.</p>
          </div>

          <div className="whitepaper-section">
            <h2>LIMITATIONS</h2>
            <ul>
              <li>The system sees the world through news coverage: what the press does not cover, it does not measure, and coverage spikes weigh more than quiet structural change.</li>
              <li>Sources are mostly English-language and Western, with some Chinese tech press.</li>
              <li>Each article is scored by a language model: scores can be wrong, and the rationale is shown so they can be checked.</li>
              <li>Automatic analysis started on 4 February 2026. Earlier values rest on about 20 older articles a month and are shown as a striped "low data" area.</li>
              <li>The indices are a monitoring instrument, not a forecast or a probability.</li>
            </ul>
          </div>

          <div className="whitepaper-section">
            <h2>CHANGELOG</h2>
            <ul>
              <li>
                <strong>Data (October 2026):</strong> Each news story now counts once. Re-titled copies of the same page, edited headlines and the same story from several outlets are merged (other outlets appear as "also reported by"); 628 duplicates were removed from the archive (about 11%) and history was recalculated. Public read-only API v1 released under CC BY 4.0. Articles older than 7 days are no longer analyzed, the hand-made starting history (June to October 2025) was removed, and the hand-drawn projections are gone from the chart.
              </li>
              <li>
                <strong>Dashboard 1.1 (October 2026):</strong> Redesigned interface. Dimensions grouped into accelerators and brakes, with colors that follow what each value means for the race. The Temporal Evolution chart now uses a true time scale and shows all 15 AI 2027 milestones (milestone markers were previously not drawn at all).
              </li>
              <li>
                <strong>Index v2.0 (October 2026):</strong> Fixed a sign error that moved the four brake dimensions in the wrong direction (regulation news lowered Model Governance). Replaced the cumulative update, which had pushed five of seven dimensions to the 0 or 3 limit, with a time-weighted recomputation. Race and Slowdown now measure separate forces, a "Balanced" status was added, and confidence is now 0-100%. Historical data since the first analyzed event was recalculated with v2.
              </li>
              <li>
                <strong>Index v1.1 (May 2026):</strong> Recalibrated weights to the 0-3 scale.
              </li>
              <li>
                <strong>Index v1.0 (February 2026):</strong> First public release: cumulative per-event update of the dimensions.
              </li>
            </ul>
          </div>

          <div className="collab-box">
            <h2><PixelIcon name="people" size={14} /> OPEN FOR COLLABORATION</h2>
            <p>
              The AI-MAGEDDON model is a living project. We invite researchers, policy analysts, and data scientists to collaborate on refining the dimensional weights, signal detection sources, and scenario definitions.
            </p>
            <p>
              Help us calibrate the instrument that measures our trajectory.
            </p>
            <a href="mailto:collaborate@ai27.org" className="contact-email">
              CONTACT RESEARCH TEAM
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WhitePaper;
